/**
 * Pendo tracking events for customer feedback
 */
export enum FeedbackEvent {
  aiAnalysisFeedback = 'AI_Analysis_Feedback',
}

/**
 * The sentiment type of the feedback being collected
 */
export enum FeedbackType {
  positive = 'positive',
  negative = 'negative',
}

/**
 * A single radio button option in the feedback form
 */
export interface FeedbackRadioOption {
  value: string;
  label: string;
}

/**
 * All localized copy needed for a single feedback form variant (positive or negative)
 */
export interface FeedbackCopy {
  title: string;
  description: string;
  radioOptions?: FeedbackRadioOption[];
  textareaLabel: string;
  textareaPlaceholder: string;
  dismissButton: string;
  submitButton: string;
  ariaLabel: string;
  tooltip: string;
  errorMessage: string;
  successMessage: string;
  successToastTitle: string;
  errorToastTitle: string;
}

/**
 * Complete feedback configuration including both positive and negative variants
 * and the Pendo tracking event to fire on submission
 */
export interface FeedbackConfig {
  positive: FeedbackCopy;
  negative: FeedbackCopy;
  tracking: FeedbackEvent;
}

/**
 * Localization structure for a single feedback type (positive or negative).
 * This should match the structure in your i18n JSON files.
 */
export interface FeedbackTypeLocalization {
  description: string;
  ariaLabel: string;
  tooltip: string;
  options: Record<string, string>;
}

/**
 * Localization for feedback form button labels
 */
export interface FeedbackButtonLabels {
  dismiss: string;
  submit: string;
}

/**
 * Localization for feedback form error messages
 */
export interface FeedbackErrorMessages {
  analyticsUnavailable: string;
}

/**
 * Localization for feedback form success messages
 */
export interface FeedbackSuccessMessages {
  submitted: string;
}

/**
 * Localization for feedback form toast titles
 */
export interface FeedbackToastTitles {
  successTitle: string;
  errorTitle: string;
}

/**
 * Complete localization structure for the feedback form.
 * This interface describes the expected shape of i18n JSON for feedback forms.
 */
export interface FeedbackFormLocalization {
  title: string;
  positive: FeedbackTypeLocalization;
  negative: FeedbackTypeLocalization;
  textareaLabel: string;
  textareaPlaceholder: string;
  buttons: FeedbackButtonLabels;
  errors: FeedbackErrorMessages;
  success: FeedbackSuccessMessages;
  toasts: FeedbackToastTitles;
}
